import type { BridgeMode } from '../use-printing';

/**
 * Legacy→bridge upgrade nudge: availability rule + snooze/seen persistence.
 *
 * This module must stay free of runtime imports (only type imports) so the
 * unit spec can run under vitest's node environment — e.g. importing Globals
 * evaluates browser-only code at module scope.
 */

/** localStorage key holding the snooze-until epoch (ms). */
export const PRINTING_UPGRADE_SNOOZE_KEY = 'printing-upgrade-snooze-until';
/** localStorage marker set when the upgrade interstitial was shown. */
export const PRINTING_UPGRADE_SEEN_KEY = 'printing-upgrade-seen';
/** Snooze duration: 7 days. */
export const SNOOZE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

/** The nudge applies only to desktop-browser sessions on the legacy plugin. */
export function isUpgradeAvailable(
  bridgeMode: BridgeMode,
  isCapacitor: boolean
): boolean {
  return bridgeMode === 'legacy' && !isCapacitor;
}

export function isSnoozeActive(
  snoozedUntilMs: number | null,
  nowMs: number
): boolean {
  return snoozedUntilMs !== null && nowMs < snoozedUntilMs;
}

/**
 * Same semantics as Globals.hasLocalStorage() without the heavy import.
 * Storage failures (disabled storage, quota, privacy mode) must never break
 * printing, so every accessor below is failure-tolerant.
 */
function getStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function getSnoozedUntil(): number | null {
  try {
    const raw = getStorage()?.getItem(PRINTING_UPGRADE_SNOOZE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setSnooze(nowMs: number): void {
  try {
    getStorage()?.setItem(
      PRINTING_UPGRADE_SNOOZE_KEY,
      String(nowMs + SNOOZE_DURATION_MS)
    );
  } catch {
    // Ignore storage failures.
  }
}

export function clearSnooze(): void {
  try {
    getStorage()?.removeItem(PRINTING_UPGRADE_SNOOZE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function markUpgradeSeen(): void {
  try {
    getStorage()?.setItem(PRINTING_UPGRADE_SEEN_KEY, '1');
  } catch {
    // Ignore storage failures.
  }
}

/** Returns whether the seen marker existed, removing it either way. */
export function consumeUpgradeSeen(): boolean {
  try {
    const storage = getStorage();
    if (!storage) return false;
    const seen = storage.getItem(PRINTING_UPGRADE_SEEN_KEY) !== null;
    storage.removeItem(PRINTING_UPGRADE_SEEN_KEY);
    return seen;
  } catch {
    return false;
  }
}
